import axios from 'axios';
import { BASE_URL, SHORTS_RENDER_PRESET_API_PATH } from 'constants/api';
import { FetchResult } from 'type/apiType';
import {
  RenderPreviewRequest,
  ShortsRenderPresetInterface,
  ShortsRenderPresetRequest,
} from '../types/shortsRenderPresetType';

const RENDER_PRESET_URL = `${BASE_URL}${SHORTS_RENDER_PRESET_API_PATH}`;

class ShortsRenderPresetService {
  public async fetchPresets(
    params?: URLSearchParams
  ): Promise<FetchResult<ShortsRenderPresetInterface>> {
    const response = await axios.get(RENDER_PRESET_URL, { params });
    return response.data;
  }

  /** 404 for both "no such id" and "belongs to another platform" — the backend does not
   *  distinguish the two, so callers must not report the preset as merely missing. */
  public async fetchPresetById(id: number): Promise<ShortsRenderPresetInterface> {
    const response = await axios.get(`${RENDER_PRESET_URL}${id}/`);
    return response.data;
  }

  public async createPreset(data: ShortsRenderPresetRequest): Promise<ShortsRenderPresetInterface> {
    const response = await axios.post(RENDER_PRESET_URL, data);
    return response.data;
  }

  /** platform_id is create-only, so callers must not include it in the update payload. */
  public async updatePreset(
    id: number,
    data: Omit<ShortsRenderPresetRequest, 'platform_id'>
  ): Promise<ShortsRenderPresetInterface> {
    const response = await axios.patch(`${RENDER_PRESET_URL}${id}/`, data);
    return response.data;
  }

  /** Linked seasons survive — the FK is SET_NULL, so only `render_preset` is cleared. */
  public async deletePreset(id: number): Promise<void> {
    await axios.delete(`${RENDER_PRESET_URL}${id}/`);
  }

  /**
   * Rendered still frame as a PNG blob. Costs a real ffmpeg pass server-side
   * (up to a 25s timeout), so callers must debounce — see RENDER_PREVIEW_DEBOUNCE_MS.
   *
   * The server caches identical requests for 5 minutes but sends `no-store`, so the
   * browser always asks.
   */
  public async fetchPreview(payload: RenderPreviewRequest, signal?: AbortSignal): Promise<Blob> {
    try {
      const response = await axios.post<Blob>(`${RENDER_PRESET_URL}preview/`, payload, {
        responseType: 'blob',
        signal,
      });
      return response.data;
    } catch (error) {
      // 4xx bodies are JSON but arrive as Blobs too, because responseType applies to
      // them as well. Parse it back so the shared `errors`/`message` readers work.
      throw await parseBlobErrorBody(error);
    }
  }
}

/**
 * Replace a Blob error body with the parsed JSON object, in place.
 * Leaves the error untouched if the body isn't a Blob or isn't JSON.
 */
const parseBlobErrorBody = async (error: unknown): Promise<unknown> => {
  const response = (error as { response?: { data?: unknown } })?.response;
  if (!response || !(response.data instanceof Blob)) {
    return error;
  }
  try {
    response.data = JSON.parse(await response.data.text());
  } catch {
    // Non-JSON body (an HTML error page, say) — leave it as it is.
  }
  return error;
};

export const shortsRenderPresetService = new ShortsRenderPresetService();

// The error payload shape (`{ code, message, errors }`) is shared with the work-order
// preset API, so the readers live there rather than being duplicated.
export {
  extractPresetFieldErrors,
  formatPresetErrorMessage,
} from 'content/shorts-preset/services/shortsPresetService';
export type { PresetFieldErrors } from 'content/shorts-preset/services/shortsPresetService';
